/* Audit-report data-stream endpoints, built on frameworksApi.ts's shared getAPI/auth/error helpers. */
import { useAuthStore } from "../../../../stores/authStore";
import {
  getAPI,
  handleSessionExpiration,
  briefHttpError,
  getAuthHeaders,
} from "../../../../api/frameworksApi";
import { API_URL, REPORTS_TIMEOUT_MS, REPORTS_URL } from "../../../../constants/index";

export interface ReportProgress {
  percent: number;
  phase: string;
}

/** Parse one SSE event block into its event name + JSON payload. */
function parseReportStreamBlock(
  block: string,
): { event: string; payload: unknown } | null {
  let eventName = "message";
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      const v = line.slice(5);
      dataLines.push(v.startsWith(" ") ? v.slice(1) : v);
    }
  }
  if (!dataLines.length) return null;
  try {
    return { event: eventName, payload: JSON.parse(dataLines.join("\n")) };
  } catch {
    return null;
  }
}

/**
 * Consume one report SSE endpoint. Shared by the SSP-wide stream and the
 * single-assessment stream, which speak the same event contract: `progress`
 * events drive the water-fill loader (via `onProgress`), `section` events
 * hand each chunk to `onSection`, and the promise resolves with the full
 * report payload delivered by the `complete` event. Rejects on `error`
 * events, network / transport failure, or AbortError when `signal` fires.
 */
const consumeReportStream = async (
  url: string,
  onProgress: (p: ReportProgress) => void,
  signal?: AbortSignal,
  onSection?: (name: string, payload: unknown) => void,
): Promise<any> => {
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "text/event-stream",
      ...getAuthHeaders(),
    },
    signal,
  });
  console.log("[report-stream] status:", res.status, res.statusText, res.url);
  if (res.status === 401) {
    handleSessionExpiration();
    const err = new Error("Session expired. Please login again.");
    err.name = "Session Expired";
    throw err;
  }
  if (!res.ok || !res.body) {
    const body = await res.text().catch(() => "");
    throw new Error(briefHttpError(res.status, body, res.statusText));
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  const handleParsed = (parsed: { event: string; payload: unknown } | null) => {
    if (!parsed) return undefined;
    if (parsed.event === "progress") {
      onProgress(parsed.payload as ReportProgress);
      return undefined;
    }
    if (parsed.event === "section") {
      const s = parsed.payload as { name?: string; payload?: unknown } | undefined;
      if (s?.name) onSection?.(s.name, s.payload);
      return undefined;
    }
    if (parsed.event === "complete") return { done: true, value: parsed.payload };
    if (parsed.event === "error") {
      throw new Error(
        (parsed.payload as { message?: string })?.message ||
        "Report generation failed",
      );
    }
    return undefined;
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, "\n");
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (!block.trim()) continue;
        const result = handleParsed(parseReportStreamBlock(block));
        if (result) return result.value;
      }
    }
    if (buffer.trim()) {
      const result = handleParsed(parseReportStreamBlock(buffer));
      if (result) return result.value;
    }
    throw new Error("Report stream ended before completion");
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
};

/**
 * Stream an SSP's audit-report data over SSE (the SSP-wide report).
 * Resolves with the same payload shape as GET /data.
 */
export const streamReport = async (
  sspId: string,
  onProgress: (p: ReportProgress) => void,
  signal?: AbortSignal,
  onSection?: (name: string, payload: unknown) => void,
): Promise<any> =>
  consumeReportStream(REPORTS_URL.dataStream(sspId), onProgress, signal, onSection);

/**
 * Stream the audit report scoped to ONE assessment plan. Same event contract
 * and payload shape as `streamReport` — only the URL differs.
 */
export const streamAssessmentReport = async (
  sspId: string,
  assessmentId: string,
  onProgress: (p: ReportProgress) => void,
  signal?: AbortSignal,
  onSection?: (name: string, payload: unknown) => void,
): Promise<any> =>
  consumeReportStream(
    REPORTS_URL.assessmentStream(sspId, assessmentId),
    onProgress,
    signal,
    onSection,
  );

/** One assessment plan as offered by the plan picker in the download dialog. */
export interface AssessmentPlanOption {
  planId: string;
  title: string;
}

/**
 * The SSP's assessment plans, used to populate the plan picker in the download
 * dialog. The auditor download streams ONE plan's report
 * (/assessment/{assessmentId}/stream), so we need a planId — the report payload
 * itself only carries plan *titles* (meta.assessmentPlans), never ids.
 *
 * Sourced from the same summary payload the reports list / report detail pages
 * read (`ReportDetails.tsx` → `REPORTS_URL.summary(sspId)`), tolerating the
 * three envelope shapes that endpoint is known to answer with: a bare array, a
 * `{ data: [...] }` wrapper, or a single summary object.
 */
export const fetchAssessmentPlans = async (
  sspId: string,
): Promise<AssessmentPlanOption[]> => {
  const res = (await getAPI(REPORTS_URL.summary(sspId))) as any;
  const summary = Array.isArray(res) ? res[0] : (res?.data ?? res);
  const plans: any[] = Array.isArray(summary?.assessmentPlans)
    ? summary.assessmentPlans
    : [];
  return plans
    .map((p) => ({ planId: String(p?.planId ?? ""), title: String(p?.title ?? "") }))
    .filter((p) => p.planId);
};

/** Executive summary for the audit report — fetched separately from the stream. */
export const fetchReportExecutiveSummary = async (
  sspId: string,
): Promise<string> => {
  const res = await getAPI(
    `${API_URL.REPORTS_SSP}/${sspId}/data/executive-summary`,
    REPORTS_TIMEOUT_MS,
  );
  if (typeof res === "string") return res;
  const r = res as Record<string, unknown> | undefined;
  const text = r?.data ?? r?.summary ?? r?.text ?? r?.executiveSummary;
  return typeof text === "string" ? text : "";
};

/** Fetch a supporting-evidence file (by resourceId) as base64, for embedding
 *  in a downloaded report / previewing inside the report iframe. */
export const fetchEvidenceBase64 = async (
  resourceId: string,
): Promise<{ fileName?: string; contentType?: string; base64: string }> => {
  const res = await fetch(
    `${window.BE_URL || ""}/api/v1/content-registry/supporting-evidence/${resourceId}/download`,
    {
      credentials: "include",
      headers: { ...getAuthHeaders() },
    },
  );
  if (!res.ok) {
    throw new Error(`Evidence fetch failed: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  const nameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  let fileName: string | undefined;
  if (nameMatch) {
    try {
      fileName = decodeURIComponent(nameMatch[1].trim());
    } catch {
      fileName = nameMatch[1].trim();
    }
  }
  const base64 = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onloadend = () => resolve(((fr.result as string) || "").split(",")[1] ?? "");
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(blob);
  });
  return { fileName, contentType: blob.type || undefined, base64 };
};
